/**
 * Import Dependency
 */
import Realisation from '@/models/Realisation'

import Vue from 'vue'
import { visibleRecordsByStatus } from '@/config'
import ChargerLocation from '@/models/ChargerLocation.js'


/**
 * Import API
 */

/**
 * Declare Variable
 */
const state = {
  /**
   * Locks the UI while loading / refreshing / sending data
   */
  loading: false,
  lastRefresh: '',

  /**
   * The search term in the list view
   */
  searchterm: '',
  currentPage: 1,
  currentStep: 1,


  /**
   * List of realisation processes
   */
  records: [],
  filteredRecordsByStatus: [],

   /**
    * Keep track of the last viewed realisation (in this session)
    */
  lastOpenedRecord: null,

  /**
   * Keep track of the currently viewed realisation process
   */
  currentOpenedRecord: null,

  /**
   * The most recent TS of the records loaded in bulk
   */
  lastRefreshTS: null,
}

const getters = {
  loading: state => state.loading,
  lastRefresh: state => state.lastRefresh,

  searchterm: state => state.searchterm,
  currentPage: state => state.currentPage,
  currentStep: state => state.currentStep,

  lastOpenedRecord: state => state.lastOpenedRecord,
  currentlyOpenedRecord: state => state.currentOpenedRecord,
  hasCurrentlyOpenedRecord: state => state.currentOpenedRecord !== null,

  /**
   * List
   */
  records: state => state.records.sort((a, b) => a.counter > b.counter ? -1: 1),
  hasRecords: state => state.records.length !== 0,
  filteredRecordsByStatus: state => state.filteredRecordsByStatus,

  /**
   * Detail
   */
  hasRecord: state => ({ uuid }) => {
    return Array.isArray(state.records) && state.records.filter(record => record.uuid === uuid).length > 0
  },
  recordByUuid: state => ({ uuid }) => {
    let records = state.records.filter(record => record.uuid === uuid)
    return records.length ? records[0] : null
  },
}
const actions = {
  loadProcessRecordsFromCBAPI: async function (
    { state, commit },
    { codes, progressCallback, refresh = null, at = null, fromTimeStamp = null },
  ) {

    if (state.loading) return null

    commit('setLoadingState', { loading: true })

    // When refreshing, we only get recent changes
    refresh = refresh === true
      ? (fromTimeStamp || state.lastRefreshTS)
      : null

    const params = {
      codes, // TODO: Find a way to avoid status code 413 (request size too large)
      fromTimeStamp: refresh,
      locationMapper: true,
      at,
    }

    const response = await this.$cbapi.utility.paginatedAPICall({
      apiCall: this.$cbapi.process.getCollectionPage,
      params,
      progressCallback,
      related: true,
    })

    if (response === false) {
      console.log('Failed to load RPs')

      commit('setLoadingState', { loading: false })
      return null
    }

    // TODO: CBAPI - TEMP HARDCODED
    let records = response.records.filter(
      record => record.flow.uuid === '53c35fb3-c0f5-439c-8924-f0e4d7bf9e47',
    )

    if (response?.related?.['charging-point-locations']) {
      commit('chargingpoints/setRecords', {
        records: response.related['charging-point-locations'].map(
          location => Object.freeze(new ChargerLocation({ data: location, cbapi: true })),
        ),
        norefresh: true,
      }, { root: true })
    }

    let lastRefreshTS = 0

    records = records.map(
      record => {
        record = new Realisation({
          cbapi: true,
          data: record,
          ref: null,
        })

        lastRefreshTS = Math.max(lastRefreshTS, record.getSyncedTS())

        return record
      })
      .filter(record => record.uuid)

    if (refresh === null) {
      commit('setRecords', { records })
    } else {
      commit('replaceRecords', { records })
    }

    if (lastRefreshTS !== 0) {
      commit('setLastRefreshTS', { lastRefreshTS })
    }
    commit('setLoadingState', { loading: false })

    return records
  },

  loadRecordByUuid: async function(
    { state, commit },
    { uuid },
  ) {
    if (state.loading) return

    commit('setLoadingState', { loading: true })

    let response = await this.$cbapi.process.getByUuid({ uuid })

    if (! response) {
      // TODO: CBAPI - better error hanlding in Vue 3
      console.log('Error - LoadRecordByUuid')
      return
    }

    const record = new Realisation({
      cbapi: true,
      data: response.data,
      ref: null,
    })

    commit('addCBAPIRecord', { record })
    commit('setLoadingState', { loading: false })
  },

  // This is a PRE-CBAPI method
  loadRecords: async ({ state, commit }, { token }) => {
    if (state.loading) return

    commit('setLoadingState', { loading: true })
    const api = await fetch('/api/realisationlist', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (! api.ok) {
      commit('setLoadingState', { loading: false })
    } else {
      const response = await api.json()

      const records = response.data.records.map(record => {
        return new Realisation({ data: record.data, ref: record.ref['@ref'].id })
      })

      commit('setRecords', {
        records,
      })
      commit('setLoadingState', { loading: false })
    }
  },
  // This is a PRE-CBAPI Method
  loadRecordsByCode: async  ({ commit }, { token, code }) => {
    const api = await fetch('/api/realisationlist_paged', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        codes: [code],
      }),
    })
    if (! api.ok) {
      throw new Error('failed')
    }

    const response = await api.json()
    const records = response.data.records.map(record => {
      return new Realisation({ data: record.data, ref: record.ref['@ref'].id })
    })

    commit('replaceRecordsByCode', {
      records, code,
    })
  },
  loadFilteredRecords ({ commit }) {
    const statuses = Object.keys(visibleRecordsByStatus).filter(status => !visibleRecordsByStatus[status])
    const records = statuses.length
      ? state.records.filter(record => !statuses.some(status => record.getStatus({ name: status })))
      : state.records

    commit('filterRecordsByStatus', { records })
  },
  updateCurrentStep ({ commit }, step) {
    commit('setCurrentStep', step)
  },
}
const mutations = {
  setLoadingState(state, { loading }) {
    state.loading = loading
  },
  setLastOpenedRecord(state, { uuid }) {
    state.lastOpenedRecord = uuid
  },
  setCurrentlyOpenedRecord(state, { uuid }) {
    state.currentOpenedRecord = uuid
  },
  setRecords(state, { records }) {
    state.records = records
    const date = new Date(Date.now())
    state.lastRefresh = `${(`0${date.getHours()}`.slice(-2))}:${(`0${date.getMinutes()}`.slice(-2))}:${(`0${date.getSeconds()}`.slice(-2))}`
  },
  replaceRecords(state, { records }) {
    let currentRecords = (state.records || [])

    records.forEach(record => {
      const uuid = record.getUuid()

      // Was this record previously loaded ?
      // If so, we need to search & replace.
      // Otherwise we can just add it to the list
      let recordIndex = currentRecords
          .findIndex(current => current.uuid === uuid)

      if (recordIndex >= 0) {

        // If the TS matches the record was already synced
        if (record.getSyncedTS() === currentRecords[recordIndex].getSyncedTS()) {
          return
        }

        currentRecords.splice(recordIndex, 1, record)
      } else {
        currentRecords.push(record)
      }
    })

    state.records = currentRecords

    const date = new Date(Date.now())
    state.lastRefresh = `${(`0${date.getHours()}`.slice(-2))}:${(`0${date.getMinutes()}`.slice(-2))}:${(`0${date.getSeconds()}`.slice(-2))}`
  },

  setLastRefreshTS(state, { lastRefreshTS }) {
    // add 1 ms, to avoid getting the record that provided the latest TS
    // TODO: CBAPI - FIX issue where 0 results are returned due to TS -
    // Details: FAUNA - Union function requires at least 1 argument(s) but 0 were given
    // CBAPI CODEBASE getUniqueRecordsFromIndex

    // state.lastRefreshTS = lastRefreshTS + 1
    state.lastRefreshTS = lastRefreshTS
  },

  /**
   * Replace all records for a municipality by municipality code
   *  If none are set, it simply adds the additional records to the collection
   */
  replaceRecordsByCode(state, { records, code }) {
    state.records = (state.records || [])
      .filter(record => record.MunicipalityCode !== code)
      .concat(records)

    const date = new Date(Date.now())
    state.lastRefresh = `${(`0${date.getHours()}`.slice(-2))}:${(`0${date.getMinutes()}`.slice(-2))}:${(`0${date.getSeconds()}`.slice(-2))}`
  },

  setSearchterm(state, { term }) {
    state.searchterm = term
  },
  setCurrentPage(state, page) {
    state.currentPage = page
  },
  setCurrentStep(state, step) {
    state.currentStep = step
  },

  replaceRecord(state, { record }) {
    let recordIndex = state.records.findIndex(current => current.uuid === record.uuid)

    if (recordIndex !== -1) {
      state.records.splice(
        recordIndex,
        1,
        new Realisation({
          data: record,
          ref: record?.ref?.['@ref']?.id || null,
          cbapi: ! record?.ref?.['@ref']?.id,
        }),
      )
    }
  },
  addCBAPIRecord(state, { record }) {
    let records = state.records
    records.push(record)
    Vue.set(state, 'records', records)
  },
  addRecord(state, { record }) {
    let records = state.records
    records.push(
      new Realisation({ data: record.data, ref: record?.ref?.['@ref']?.id || null }),
    )
    Vue.set(state, 'records', records)
  },

  // This is PRE-CBAPI Method
  updateRecordComments(state, { ref, comments }) {
    let index = state.records.findIndex(record => record.ref === ref)
    if (index < 0) return

    let record = state.records[index]

    record.comments = comments
  },
  // This is PRE-CBAPI Method
  updateRecordLocation(state, { record }) {
    let index = state.records.findIndex(currentRecord => currentRecord.ref === record.ref)
    if (index < 0) return

    let currentRecord = state.records[index]
    currentRecord.Locations[0].Location.chargingpointUuid = record.Locations[0].Location.chargingpointUuid
  },
  filterRecordsByStatus (state, { records }) {
    state.filteredRecordsByStatus = records
  },
}

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
